import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductFruits as BaseProductFruits } from 'react-product-fruits';
import { useNavigate } from 'react-router';
import { match } from 'ts-pattern';

import { useUser } from '~/api/user';
import { planIds } from '~/constants/plans';

type Props = {
  workspaceCode: string;
};

export const ProductFruits = ({ workspaceCode }: Props) => {
  const { data: user } = useUser();

  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  const productFruitsUser = useMemo(() => {
    const primaryActivity = user.activities.find((activity) => activity.isPrimaryActivity);
    const secondaryActivities = user.activities
      .filter((activity) => !activity.isPrimaryActivity)
      .map(({ id }) => t(`common:activities.${id}.label`));
    const invitationsCount = user.invitations.filter(({ status }) => status !== 'declined').length;
    const invitorDiscountsCount = user.invitorDiscounts.filter(({ isActive }) => isActive).length;

    return {
      username: user.email,
      firstname: user.firstName,
      lastname: user.lastName,
      email: user.email,
      signUpAt: user.registeredAt,
      props: {
        primaryActivity: primaryActivity ? t(`common:activities.${primaryActivity.id}.label`) : '',
        secondaryActivities,
        vatExempt: !!user.vatExempt,
        invitorDiscounts: invitorDiscountsCount,
        invitations: invitationsCount,
        isCraftCommunityEnabled: user.communityProfile.enabled,
        isNpsShown: user.showNps,
        plan: match(user.planId)
          .with(planIds.earlyAdopter, () => 'early adopter')
          .with(planIds.shelter, () => 'shelter')
          .exhaustive(),
        addressLastVerifiedAt: user.appSettings.addressLastVerifiedAt,
      },
    };
  }, [t, user]);

  return (
    <BaseProductFruits
      config={{
        customNavigation: {
          use: true,
          navigate: (location) => (location.startsWith('/') ? navigate(location) : window.open(location, '_self')),
          onGet: () => window.location.href,
        },
      }}
      language={user.lang}
      user={productFruitsUser}
      workspaceCode={workspaceCode}
    />
  );
};
